import { useEffect, useState, useRef } from "react";
import { Link } from "wouter";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import LogoAnimation from "@/components/LogoAnimation";

const CARD = {
  backgroundColor: "#FFFFFF",
  border: "1px solid #DCE3ED",
  boxShadow: "0 4px 24px 0 rgba(0,0,0,0.06)",
};

type State = "verifying" | "success" | "error";

export default function VerifyEmail() {
  const [state, setState] = useState<State>("verifying");
  const [message, setMessage] = useState("");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // guard React 18 StrictMode double-invoke (token is single-use)
    ran.current = true;
    const token = new URLSearchParams(window.location.search).get("token") || "";
    if (!token) {
      setState("error");
      setMessage("This verification link is missing its token.");
      return;
    }
    (async () => {
      try {
        const res = await fetch("/api/auth/verify-email", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ token }),
        });
        const data = await res.json().catch(() => ({}));
        if (res.ok) {
          setState("success");
          setMessage(data.message || "Email verified.");
        } else {
          setState("error");
          setMessage(data.message || "This verification link is invalid or has expired.");
        }
      } catch {
        setState("error");
        setMessage("Something went wrong verifying your email.");
      }
    })();
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: "#F7F9FC" }} data-testid="verify-email-page">
      <div className="w-full max-w-sm">
        <div className="rounded-2xl px-8 py-10 text-center" style={CARD}>
          <div className="flex justify-center mb-6"><LogoAnimation size={110} /></div>

          {state === "verifying" && (
            <div data-testid="verify-loading">
              <div className="flex justify-center mb-4"><Loader2 className="h-10 w-10 animate-spin" style={{ color: "#6366F1" }} /></div>
              <h1 className="text-lg font-semibold" style={{ color: "#1A1A1A" }}>Verifying your email…</h1>
            </div>
          )}

          {state === "success" && (
            <div data-testid="verify-success">
              <div className="flex justify-center mb-4"><CheckCircle2 className="h-10 w-10" style={{ color: "#16a34a" }} /></div>
              <h1 className="text-lg font-semibold" style={{ color: "#1A1A1A" }}>Email verified</h1>
              <p className="text-sm mt-2" style={{ color: "#6B6B6B" }}>You're all set. Thanks for confirming your address.</p>
              <Link href="/" className="inline-block mt-6 h-10 px-5 rounded-lg text-sm font-medium text-white leading-10" style={{ backgroundColor: "#6366F1" }}>
                Go to AdMoji
              </Link>
            </div>
          )}

          {state === "error" && (
            <div data-testid="verify-error">
              <div className="flex justify-center mb-4"><XCircle className="h-10 w-10" style={{ color: "#B04040" }} /></div>
              <h1 className="text-lg font-semibold" style={{ color: "#1A1A1A" }}>Verification failed</h1>
              <p className="text-sm mt-2" style={{ color: "#6B6B6B" }}>{message}</p>
              <p className="text-xs mt-4" style={{ color: "#5F6D82" }}>
                You can request a fresh link from the banner inside the app once signed in.
              </p>
              <Link href="/" className="inline-block mt-5 text-sm font-medium" style={{ color: "#6366F1" }}>Go to AdMoji</Link>
            </div>
          )}
        </div>
        <p className="text-center text-xs mt-6" style={{ color: "#B0B0AE" }}>Admoji Ad Manager</p>
      </div>
    </div>
  );
}
